import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ICheckpointAction, ICheckpointActionAlertRecipient } from '@fi-shared/checklist/checklist-v2';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { CheckpointActionType } from '@fi-shared/checklist/checklist-enum';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-checkpoint-edit-actions-tab',
    templateUrl: './actions-tab.component.html',
    styleUrls: ['./actions-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-edit-actions-tab' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiCheckpointEditActionsTabComponent }],
    standalone: false,
})
export class FiCheckpointEditActionsTabComponent extends FiComponentsContainer {
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() checkpointActions: ICheckpointAction[] = [];
    @Input() checkpointName: string;
    @Input() checklistName: string;
    @Output() checkpointActionsChange = new EventEmitter<ICheckpointAction[]>();
    readonly CheckpointActionType = CheckpointActionType;
    protected readonly ObjectTypes = ObjectTypes;

    constructor(
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private appTextService: AppTextService,
        private fiSnackbarService: FiSnackBarService,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {
        super();
    }

    protected openAddNewCheckpointAction(actionType: CheckpointActionType): void {
        const newCheckpointAction: ICheckpointAction = this.fiCheckpoint2HelperService.getDefaultAction(actionType);

        this.getCheckpointActionEditorDialogByType(newCheckpointAction)
            .onYesButtonClick((newCheckpointAction: ICheckpointAction) => {
                this.checkpointActions.push(newCheckpointAction);
                this.checkpointActionsChange.emit(this.checkpointActions);
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected editCheckpointAction(actionIndex: number): void {
        this.getCheckpointActionEditorDialogByType(this.checkpointActions[actionIndex])
            .onYesButtonClick((newCheckpointAction: ICheckpointAction) => {
                this.checkpointActions[actionIndex] = newCheckpointAction;
                this.checkpointActionsChange.emit(this.checkpointActions);
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected openConfirmDeleteActionDialog(actionIndex: number): void {
        this.fiCheckpoint2HelperService
            .getConfirmDeleteCheckpointActionsDialog()
            .onYesButtonClick(() => this.deleteAction(actionIndex));
    }

    protected getSelectedItemIds(alertRecipient: ICheckpointActionAlertRecipient): number[] {
        return this.fiCheckpoint2HelperService.getCheckpointTaskRecipientIds(alertRecipient);
    }

    private deleteAction(actionIndex: number) {
        this.checkpointActions.splice(actionIndex, 1);
        this.checkpointActionsChange.emit(this.checkpointActions);
        this.fiSnackbarService.showSnackbar({
            textMessage: this.appTextService.get('checklist.checkpoint_action_deleted'),
        });
    }

    private getCheckpointActionEditorDialogByType(
        checkpointAction: ICheckpointAction,
    ): IFiDialogResult<ICheckpointAction> {
        return this.fiCheckpoint2HelperService.getCheckpointActionEditorDialogByType(
            checkpointAction,
            this.accessRightFilterCollection,
            false,
            this.checkpointName,
            this.checklistName,
        );
    }
}
